import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { MeResponse } from './AuthProvider';
import { SubscribeDialog } from './SubscribeDialog';
import { ReactivateDialog } from './ReactivateDialog';
import {
  blockedScreenCopy, exportNoteText, FLUIDSOL_CONTACT_EMAIL, hasDebt, reactivationCopy, type ReactivationInfo,
} from './billingNotice';
import { ExportDataPanel } from '../components/ExportDataPanel';

interface Props {
  me: MeResponse | null;
  expiredAt: string | null;
  message: string;
  onLogout: () => void;
  /** §17.6 — habilita el flujo de suscripción (selector mensual/anual → Checkout).
   *  Solo lo activa App.tsx para SUPER_USER (el checkout es del admin). */
  canSubscribe?: boolean;
  /** T4 — llegó por la liga del último correo (/pago/reactivar): abre el pago del adeudo. */
  openReactivate?: boolean;
}

/**
 * Pantalla bloqueante post-trial. NO permite navegar al Dashboard.
 * El user solo puede: suscribirse (selector mensual/anual → Stripe Checkout) /
 * pagar por transferencia, o cerrar sesión. (§17.6 — el cobro vive en Stripe.)
 */
export function TrialExpiredScreen({ me, expiredAt, message, onLogout, canSubscribe, openReactivate }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const slug = me?.tenant.slug ?? '';
  const suspended = !!me?.tenant.billing?.suspendedAt;
  // T4 (opción A, 09/10) — con la cuenta suspendida, el admin reactiva pagando el adeudo
  // dentro de la app (no Checkout: abriría una segunda suscripción, M-09).
  const [reactivation, setReactivation] = useState<ReactivationInfo | null>(null);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  useEffect(() => {
    if (!suspended || !canSubscribe) return;
    api<ReactivationInfo>('/billing/reactivation', { silentError: true })
      .then((r) => { setReactivation(r); if (openReactivate && r.needed) setReactivateOpen(true); })
      .catch(() => { /* sin datos de Stripe: queda transferencia/contacto */ });
  }, [suspended, canSubscribe, openReactivate]);
  const debt = hasDebt(reactivation);
  // T4 — prueba terminada vs suscripción suspendida por cobranza (TyC §8.8).
  const base = blockedScreenCopy(me?.tenant.billing);
  const rc = reactivationCopy(reactivation);
  const copy = rc ? { ...base, title: rc.title, detail: rc.detail + exportNoteText(me?.tenant.billing) } : base;
  // L-14 (TyC §15) — 30 días tras la suspensión el admin descarga sus datos (por vertical).
  const billing = me?.tenant.billing;
  const canExport = me?.role === 'SUPER_USER' && !!billing?.suspendedAt && !!billing.exportUntil
    && new Date(billing.exportUntil).getTime() > Date.now();
  const exportUntilLabel = billing?.exportUntil
    ? new Date(billing.exportUntil).toLocaleDateString('es-MX', { timeZone: 'America/Mexico_City' })
    : '';
  const mailto =
    `mailto:${FLUIDSOL_CONTACT_EMAIL}?subject=` +
    encodeURIComponent(`Reactivación FluidSol ${slug}`) +
    `&body=` +
    encodeURIComponent(
      `Hola,\n\nMi trial de FluidSol terminó${expiredAt ? ` el ${expiredAt}` : ''}.\n` +
      `Workspace: ${slug || '(sin slug)'}\n` +
      `Email: ${me?.email ?? ''}\n\n` +
      `Quiero reactivar la cuenta y conocer los planes.`,
    );

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAF7] px-4">
      <div className="card-soft w-full max-w-md rounded-2xl border border-slate-200/80 bg-white p-8 text-center">
        <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-rose-50">
          <span className="text-2xl">⏰</span>
        </div>
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
          {copy.label}
        </p>
        <h1 className="mt-1 font-serif text-2xl tracking-tight text-slate-900">
          {copy.title}
        </h1>
        {expiredAt && !me?.tenant.billing?.suspendedAt && (
          <p className="mt-1 text-sm text-slate-500">Vencimiento: {expiredAt}</p>
        )}
        <p className="mt-4 text-sm text-slate-600">{me?.tenant.billing?.suspendedAt ? copy.detail : message}</p>

        <div className="mt-6 space-y-2">
          {/* §17.6 — abre el selector mensual/anual; el Checkout hospedado de Stripe
              se genera fresco al elegir (Stratos no toca datos de tarjeta). */}
          {canSubscribe && reactivation?.needed && (
            <button
              onClick={() => setReactivateOpen(true)}
              className="block w-full rounded-xl bg-slate-900 px-5 py-3 text-sm font-medium text-white hover:bg-slate-800"
            >
              Pagar adeudo y reactivar
            </button>
          )}
          {canSubscribe && (!suspended || (reactivation && !debt)) && (
            <button
              onClick={() => setDialogOpen(true)}
              className="block w-full rounded-xl bg-slate-900 px-5 py-3 text-sm font-medium text-white hover:bg-slate-800"
            >
              Suscribirme ahora
            </button>
          )}
          {suspended && !canSubscribe && (
            <p className="text-sm text-slate-500">Pide al administrador del negocio que pague el adeudo para reactivar la cuenta.</p>
          )}
          <a
            href={mailto}
            className="block w-full rounded-xl bg-white px-5 py-3 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
          >
            Pagar por transferencia / hablar con ventas
          </a>
          <button
            onClick={onLogout}
            className="block w-full rounded-xl bg-white px-5 py-3 text-xs font-medium text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50"
          >
            Cerrar sesión
          </button>
        </div>

        {/* L-14 (TyC §15) — 30 días tras la suspensión: cada vertical descarga lo suyo,
            debajo de la acción principal (pagar el adeudo y reactivar). */}
        {canExport && (
          <div className="mt-6 border-t border-slate-100 pt-5 text-left">
            <p className="mb-2 text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
              Descarga tus datos (hasta el {exportUntilLabel})
            </p>
            <ExportDataPanel compact />
          </div>
        )}

        <p className="mt-6 text-[11px] text-slate-400">
          Dudas: {FLUIDSOL_CONTACT_EMAIL}. Tu información se conserva conforme a los Términos y Condiciones.
        </p>
      </div>
      <SubscribeDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      {reactivateOpen && reactivation && (
        <ReactivateDialog info={reactivation} onClose={() => setReactivateOpen(false)} />
      )}
    </div>
  );
}
